// app/components/causeOfCalls/CauseModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const CauseModal = ({ isOpen, onClose, onSave, cause, allCauses, schemeId }) => {
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        parent_id: null,
        is_active: true,
        client_scheme_id: schemeId
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (cause) {
            setFormData({
                code: cause.code,
                name: cause.name,
                description: cause.description || '',
                parent_id: cause.parent_id,
                is_active: cause.is_active !== false,
                client_scheme_id: cause.client_scheme_id || schemeId
            });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                parent_id: null,
                is_active: true,
                client_scheme_id: schemeId
            });
        }
        setErrors({});
    }, [cause, schemeId]);

    const validate = () => {
        const newErrors = {};

        if (!formData.code.trim()) {
            newErrors.code = 'Code is required';
        } else if (!/^[A-Z_0-9]+$/.test(formData.code)) {
            newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.client_scheme_id) {
            newErrors.client_scheme_id = 'Scheme is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving cause:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter out the current cause and its descendants from parent options
    const getAvailableParents = () => {
        if (!cause) return allCauses;

        const excludeIds = new Set([cause.id]);
        const findDescendants = (parentId) => {
            allCauses.filter(c => c.parent_id === parentId).forEach(child => {
                excludeIds.add(child.id);
                findDescendants(child.id);
            });
        };
        findDescendants(cause.id);

        return allCauses.filter(c => !excludeIds.has(c.id));
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={cause ? 'Edit Cause' : 'Add New Cause'}
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                        Code
                    </label>
                    <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({
                            ...formData,
                            code: e.target.value.toUpperCase()
                        })}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-300'
                            }`}
                        disabled={!!cause}
                        placeholder="e.g., ENGINE_ISSUE"
                    />
                    {errors.code && (
                        <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                        Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="e.g., Engine Issue"
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Describe this cause of call"
                    />
                </div>

                <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                        Parent Cause
                    </label>
                    <select
                        value={formData.parent_id || ''}
                        onChange={(e) => setFormData({
                            ...formData,
                            parent_id: e.target.value ? parseInt(e.target.value) : null
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">None (Top Level)</option>
                        {getAvailableParents().map(parentCause => (
                            <option key={parentCause.id} value={parentCause.id}>
                                {parentCause.name} ({parentCause.code})
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (cause ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CauseModal;  